import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, onSnapshot, orderBy, query, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';

const fmtDate = (v) => {
  try {
    const date = v?.toDate ? v.toDate() : (typeof v === 'string' ? new Date(v) : null);
    return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : 'Just received';
  } catch { return 'Date unavailable'; }
};
const ts = v => v?.toMillis ? v.toMillis() : (typeof v === 'string' ? new Date(v).getTime() || 0 : 0);
const pill = 'inline-flex rounded-full px-2.5 py-1 text-xs font-semibold bg-slate-100 text-slate-700';
const button = 'inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50';
const action = 'inline-flex items-center justify-center rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50';
const PORTAL_SIGNUP = 'https://portal.chukologic.com/signup';
const VERIFY_EMAIL = 'https://portal.chukologic.com/verify-email';

/** A single visual inbox for website inquiries and logged-in booking requests.
 * Website inquiries remain serviceRequests until a verified portal account is attached.
 */
export default function QuoteRequests({ onReviewJob }) {
  const [website, setWebsite] = useState([]);
  const [portal, setPortal] = useState([]);
  const [filter, setFilter] = useState('action');
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');
  const [load, setLoad] = useState({ website: true, portal: true });

  useEffect(() => {
    const a = onSnapshot(query(collection(db,'serviceRequests'),orderBy('createdAt','desc')),
      s => { setWebsite(s.docs.map(d => ({id:d.id,...d.data()}))); setLoad(v => ({...v, website:false})); },
      e => {setErr(`Website inquiries: ${e.message}`); setLoad(v => ({...v, website:false}));});
    const b = onSnapshot(query(collection(db,'workOrders'),orderBy('createdAt','desc')),
      s => { setPortal(s.docs.map(d => ({id:d.id,...d.data()}))); setLoad(v => ({...v, portal:false})); },
      e => {setErr(`Portal bookings: ${e.message}`); setLoad(v => ({...v, portal:false}));});
    return () => {a();b();};
  }, []);

  const items = useMemo(() => ([
    ...website.map(x => ({...x, kind:'website', contact:x.contactName || 'Website inquiry', email:x.contactEmail, created:x.createdAt})),
    ...portal.filter(x => ['pending','under_review','quote_ready','quote_sent','revision_requested'].includes(x.status))
      .map(x => ({...x, kind:'portal', contact:x.clientName || x.clientEmail || 'Portal customer', email:x.clientEmail, created:x.createdAt})),
  ]).sort((a,b) => ts(b.created) - ts(a.created)), [website,portal]);
  const actionable = x => x.kind === 'website' ? ['pending', 'responded'].includes(x.status) : ['pending', 'under_review', 'quote_ready','revision_requested'].includes(x.status);
  const visible = filter === 'all' ? items : (filter === 'website' ? items.filter(x => x.kind==='website') : items.filter(actionable));

  const convert = async id => {
    setBusyId(id);setErr('');
    try {
      const fn = httpsCallable(getFunctions(), 'convertPublicInquiry');
      await fn({ inquiryId:id });
    } catch(e) {
      setErr(e.message || 'Could not convert inquiry.');
    } finally { setBusyId(null); }
  };
  const markContacted = async id => {
    setErr('');
    try {await updateDoc(doc(db,'serviceRequests',id), { status:'responded', updatedAt:new Date() });}
    catch(e) {setErr(e.message||'Could not update request.');}
  };
  const invite = x => {
    const subject = encodeURIComponent('Your Chuko Logic service request — portal access');
    const body = encodeURIComponent(`Hello ${x.contactName || ''},\n\nThank you for contacting Chuko Logic. To manage your written quote and service updates securely, please register at ${PORTAL_SIGNUP} using this email address (${x.contactEmail}). After registering, sign in and verify your email at ${VERIFY_EMAIL}.\n\nWe'll let you know when your quote is ready.\n\nChuko Logic\ndispatch@chukologic.com\n(920) 724-1438`);
    return `mailto:${x.contactEmail}?subject=${subject}&body=${body}`;
  };

  return <section className="space-y-5" aria-label="Unified service requests">
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div><h2 className="text-2xl font-bold text-slate-900">Service requests</h2>
      <p className="mt-1 text-slate-600">Website inquiries and client portal bookings in one place.</p></div>
      <div className="flex gap-2" role="group" aria-label="Filter inquiries">
        {[[ 'action','Needs attention'],['website','Website'],['all','All']].map(([id,label]) =>
          <button key={id} onClick={()=>setFilter(id)} className={`rounded-lg px-4 py-2 text-sm font-semibold border ${filter === id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300'}`}>{label}</button>
        )}
      </div>
    </header>
    {err && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-800">{err}</p>}
    {load.website || load.portal ? <p className="text-slate-600">Loading requests…</p> : visible.length === 0 ?
      <p className="rounded-xl border border-slate-200 bg-white p-8 text-slate-600">No requests in this view.</p> :
      <div className="grid gap-3">
        {visible.map(x => <article key={`${x.kind}:${x.id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className={pill}>{x.kind === 'website' ? 'Public website' : 'Client portal'}</span>
                <span className={pill}>{(x.status || 'pending').replace(/[_-]/g,' ')}</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900">{x.serviceType || 'IT service request'}</h3>
              <p className="mt-1 text-sm text-slate-600">{x.contact} · {x.location || 'Location not provided'} · {fmtDate(x.created)}</p>
              <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap break-words">{x.description}</p>
              {x.email && <p className="mt-2 text-sm"><a className="text-blue-700 underline" href={`mailto:${x.email}`}>{x.email}</a></p>}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            {x.kind === 'portal' ? <>
              {onReviewJob && <button className={action} type="button" onClick={()=>onReviewJob(x)}>Review / Quote</button>}
              <Link className={button} to={`/jobs/${x.id}`}>View work order</Link>
            </> : <>
              {x.linkedJobId ? <Link className={action} to={`/jobs/${x.linkedJobId}`}>Open linked work order</Link> : <>
                {x.contactEmail && <a className={button} href={invite(x)}>Invite to portal</a>}
                {x.status==='pending' && <button className={button} onClick={()=>markContacted(x.id)}>Mark contacted</button>}
                <button className={action} disabled={busyId===x.id} onClick={()=>convert(x.id)}>
                  {busyId===x.id ? 'Converting…' : 'Convert to work order'}
                </button>
              </>}
              <p className="w-full text-xs text-slate-500">Conversion requires the customer to create a portal account and verify the same email address. No customer account is created automatically.</p>
            </>}
          </div>
        </article>)}
      </div>}
  </section>;
}
